import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';

// Smallest bottom inset we treat as "the on-screen keyboard is open". Filters out
// the few-pixel wobble Safari produces when it collapses its address/tool bars
// during scroll.
export const KEYBOARD_OPEN_THRESHOLD = 120;

// Delay before re-syncing the iOS caret after focus. A keyboard-anchored row
// reflows once the keyboard opens and the layout/visual viewport settle; iOS
// Safari caches the caret's paint rect from the row's pre-move position, so the
// caret is left ~10px off until the next repaint (e.g. an app switch). We wait
// for the keyboard animation + any scroll pin to settle, then force a repaint to
// drag the caret back in.
export const CARET_RESYNC_DELAY_MS = 350;

// Gap left between the keyboard-pinned row and the top of the keyboard region
// when we anchor the row to the keyboard on iOS. Lives in CSS as
// --keyboard-pinned-bottom-padding; this reference reads from that single source.
const KEYBOARD_PINNED_BOTTOM_PADDING_VAR =
  'var(--keyboard-pinned-bottom-padding)';

interface KeyboardViewport {
  // Bottom inset (px) the on-screen keyboard covers; 0 when closed.
  inset: number;
  // Top of the keyboard region in layout-viewport coords, or null when the
  // browser resizes the layout for the keyboard (so fixed elements aren't
  // overlaid and no top-anchoring is needed).
  visualBottom: number | null;
  // Whether the on-screen keyboard is actually open. True only when focusing the
  // input shrank the viewport (an iOS overlay inset, or an Android layout
  // resize). A desktop browser with no soft keyboard never shrinks it, so this
  // stays false and the row keeps its resting position.
  open: boolean;
  // How far an Android-style keyboard shrank the layout viewport (px) — i.e. how
  // far a fixed bottom:0 element was lifted above the keyboard. 0 on iOS (the
  // keyboard overlays; the layout doesn't resize) and when closed. Lets the dock
  // translate the bottom-pinned stack back down behind the keyboard.
  layoutDrop: number;
}

/**
 * Shared keyboard tracker: while `active`, watches the VisualViewport and reports
 * whether the on-screen keyboard is open plus the overlay metrics (see
 * KeyboardViewport). Single source of truth for "is the keyboard up", so callers
 * react to the keyboard actually opening rather than to mere focus — which is the
 * difference on a desktop browser, where focusing a field opens no keyboard.
 */
function useKeyboardViewport(active: boolean): KeyboardViewport {
  const [metrics, setMetrics] = useState<KeyboardViewport>({
    inset: 0,
    visualBottom: null,
    open: false,
    layoutDrop: 0,
  });
  // Tallest layout viewport seen (keyboard closed). An Android-style keyboard
  // resizes the layout shorter; measuring against the remembered full height
  // detects that even if focus moves between fields while the keyboard stays up.
  const fullLayoutRef = useRef(0);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!active || !vv) {
      setMetrics({ inset: 0, visualBottom: null, open: false, layoutDrop: 0 });
      return;
    }
    const update = () => {
      // Layout viewport height (stable while the keyboard is up, unlike
      // window.innerHeight which shrinks on iOS). Fall back to innerHeight where
      // clientHeight isn't laid out (jsdom tests).
      const layoutHeight =
        document.documentElement.clientHeight || window.innerHeight;
      fullLayoutRef.current = Math.max(fullLayoutRef.current, layoutHeight);
      const inset = Math.max(
        0,
        Math.round(layoutHeight - vv.height - vv.offsetTop)
      );
      // The keyboard is open when it overlays the layout (iOS: a visual-viewport
      // inset) or when it resized the layout shorter (Android: the layout dropped
      // below its full height). A desktop browser does neither on focus, so the
      // row never moves.
      const layoutDrop = fullLayoutRef.current - layoutHeight;
      const open =
        inset >= KEYBOARD_OPEN_THRESHOLD ||
        layoutDrop >= KEYBOARD_OPEN_THRESHOLD;
      if (!open) {
        setMetrics({
          inset: 0,
          visualBottom: null,
          open: false,
          layoutDrop: 0,
        });
        return;
      }
      // Only the overlay case needs the iOS top-anchor; a resized layout already
      // puts the row above the keyboard via its resting CSS bottom.
      setMetrics({
        inset,
        visualBottom:
          inset >= KEYBOARD_OPEN_THRESHOLD
            ? Math.round(vv.offsetTop + vv.height)
            : null,
        open: true,
        layoutDrop: Math.max(0, Math.round(layoutDrop)),
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [active]);

  return metrics;
}

/**
 * How far (px) the bottom-pinned footer stack must translate DOWN to stay glued
 * to the true screen bottom — behind the on-screen keyboard — honoring
 * FooterDockRow's pin="bottom" contract ("stays put and is allowed to be hidden
 * behind the keyboard").
 *
 * Only an Android-style keyboard (interactive-widget=resizes-content) shrinks the
 * layout viewport, which lifts a fixed bottom:0 element up above the keyboard;
 * this returns that lift so the dock can cancel it out. On iOS the keyboard
 * overlays the layout (no resize), so a bottom:0 stack already rests behind the
 * keyboard and this stays 0 — as it does when the keyboard is closed.
 *
 * Unlike useKeyboardPin, this is NOT gated on a footer input holding focus: the
 * keyboard is usually opened by a field in the page body, not in the dock.
 */
export function useBottomPinnedKeyboardOffset(): number {
  const { layoutDrop } = useKeyboardViewport(true);
  // Only a real layout resize (Android) should move the stack; a sub-threshold
  // drop is Safari toolbar wobble, not the keyboard.
  return layoutDrop >= KEYBOARD_OPEN_THRESHOLD ? layoutDrop : 0;
}

export interface KeyboardPin {
  /**
   * Inline style to merge over the keyboard row. On iOS, when the browser does
   * NOT resize the layout for the keyboard, this anchors the row by its bottom
   * edge one gap above the keyboard region (`bottom` + `top: auto`) — independent
   * of the row's height, so a tall field editor clears the keyboard and its
   * accessory bar. Otherwise it is undefined and the row keeps its CSS resting
   * position.
   */
  anchorStyle: React.CSSProperties | undefined;
  /**
   * Re-syncs the iOS caret after the row reflows above the keyboard. No-op off
   * iOS.
   */
  correctCaret: (input: HTMLInputElement | null) => void;
  /**
   * Whether the on-screen keyboard is actually open (the viewport shrank for it).
   * Drives the row's keyboard-up state — false on a desktop browser with no soft
   * keyboard, so merely focusing the field does not move the row.
   */
  keyboardOpen: boolean;
}

/**
 * Internal keyboard reconciliation for FooterDockRow's keyboard tier. Given
 * whether the row currently holds keyboard focus, it tracks the on-screen
 * keyboard via the VisualViewport API and:
 *  - anchors the row above the keyboard on iOS (where the layout doesn't resize),
 *  - pins the document to the top so iOS can't scroll the page to chase the fixed
 *    input,
 *  - publishes the keyboard inset as `--footer-keyboard-inset` (px) on the
 *    document element so scrolling lists can extend their bottom padding, and
 *  - corrects the iOS caret position after the row reflows.
 * All of it without the client wiring anything.
 */
export function useKeyboardPin(active: boolean): KeyboardPin {
  const metrics = useKeyboardViewport(active);
  const { isIOS } = useDeviceInfo();

  // iOS scrolls the whole document to keep the focused input "in view" when the
  // keyboard opens — even though it is already visible — which drags the page
  // content (and any sticky header) up under the toolbar. The row is positioned
  // independently, so pin the document to the top while active.
  useEffect(() => {
    if (!active) return;
    const pinTop = () => {
      const el = document.scrollingElement;
      if (el && el.scrollTop !== 0) el.scrollTop = 0;
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };
    pinTop();
    window.addEventListener('scroll', pinTop, { passive: true });
    window.visualViewport?.addEventListener('scroll', pinTop);
    window.visualViewport?.addEventListener('resize', pinTop);
    return () => {
      window.removeEventListener('scroll', pinTop);
      window.visualViewport?.removeEventListener('scroll', pinTop);
      window.visualViewport?.removeEventListener('resize', pinTop);
    };
  }, [active]);

  // Publish the keyboard inset so scrolling lists can extend their bottom padding
  // (via var(--footer-keyboard-inset)) to clear the keyboard and the lifted row.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--footer-keyboard-inset',
      `${active ? metrics.inset : 0}px`
    );
    return () => root.style.setProperty('--footer-keyboard-inset', '0px');
  }, [active, metrics.inset]);

  // Corrects the iOS caret position for the input that moves when the keyboard
  // opens (see CARET_RESYNC_DELAY_MS). No-op off iOS, where the bug doesn't occur.
  const correctCaret = useCallback(
    (input: HTMLInputElement | null) => {
      if (!isIOS || !input) return;
      window.setTimeout(() => {
        // Bail if focus moved on while we waited.
        if (document.activeElement !== input) return;
        // Force a compositing repaint (transform nudge + reflow), then re-assert
        // the selection so WebKit recomputes the caret rect at the row's final
        // spot. Non-disruptive (no blur/refocus) so the keyboard doesn't flicker.
        input.style.transform = 'translateZ(0)';
        void input.offsetHeight;
        input.style.transform = '';
        const end = input.value.length;
        input.setSelectionRange(end, end);
      }, CARET_RESYNC_DELAY_MS);
    },
    [isIOS]
  );

  const anchorStyle: React.CSSProperties | undefined =
    metrics.visualBottom !== null
      ? {
          // Anchor by the BOTTOM edge so the row floats one gap above the keyboard
          // region regardless of its height — a tall field editor would otherwise
          // overshoot a fixed-height top anchor and extend down into the keyboard
          // (and its iOS input-accessory bar). The keyboard region occupies the
          // bottom `inset` of the (unresized) layout viewport. `inset` is a LIVE
          // measurement, never an assumed keyboard/accessory-bar height: the bar
          // is suppressed only in the native wrapper and shows on Safari web
          // (see accessoryBar.ts), so the measured inset includes it exactly when
          // present — and re-measures via the viewport resize listener if the
          // region changes mid-focus — so the row clears whatever is actually on
          // screen with no dead space reserved for a bar that isn't there.
          bottom: `calc(${metrics.inset}px + ${KEYBOARD_PINNED_BOTTOM_PADDING_VAR})`,
          top: 'auto',
        }
      : undefined;

  return { anchorStyle, correctCaret, keyboardOpen: metrics.open };
}
